import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  InputBase,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { EmptyState, InlineMessage, PageContainer, PageHeader, SectionCard, TableSkeleton } from '../components/ui';
import { SegmentedControl } from '../components/mapping/v2/v2Ui';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { tokens } from '../theme/tokens';
import { useToast } from '../contexts/ToastContext';
import { ORG_FEATURE_FLAGS_QUERY_KEY } from '../hooks/useOrgFeatureFlags';
import { flagDescriptionParts } from '../utils/flagDescription';
import { OrganizationPicker } from '../components/adminFeatureFlags/OrganizationPicker';
import { FLAG_GRID_COLUMNS, FlagRow } from '../components/adminFeatureFlags/FlagRow';
import {
  FeatureFlagDefinition,
  FLAG_AREAS,
  FlagArea,
  globalValueOf,
  GlobalSetting,
  organizationAdminPath,
  OrgOverridesResponse,
  RegistryResponse,
  ScopedOrganization,
} from '../components/adminFeatureFlags/featureFlagTypes';

/**
 * Admin → System → Feature Flags (persona: Alex, admin shell only).
 *
 * One card: a scope bar that picks the organization whose overrides are being
 * set, a filter bar, and a Flag · Global default · Override list whose rows
 * expand to the flag's When on / When off text. Every view setting lives in
 * the query string (`org`, `q`, `area`, `state`, `sort`) so a link opens on
 * the same view; `tab` is already the System page's own tab, so the state
 * filter is `state`.
 */

const STATE_TABS = ['all', 'on', 'off', 'plan', 'ov'] as const;
type StateTab = (typeof STATE_TABS)[number];
const SORTS = ['name', 'new', 'old', 'area'] as const;
type SortKey = (typeof SORTS)[number];
const DEFAULTS: Record<string, string> = { state: 'all', sort: 'name' };
/** Below this card width the three columns stack under the flag. */
const NARROW_WIDTH = 820;

const REGISTRY_KEY = ['admin-feature-flags-registry'] as const;
const orgKey = (orgId: string | undefined) => ['admin-feature-flags-org', orgId] as const;

const pick = <T extends string>(values: readonly T[], raw: string | null, fallback: T): T =>
  raw && (values as readonly string[]).includes(raw) ? (raw as T) : fallback;

async function putJson(path: string, body: unknown, fallbackError: string) {
  const headers = await getAuthHeaders();
  const res = await makeApiCall(path, { method: 'PUT', headers, body: JSON.stringify(body) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || err.message || fallbackError);
  }
  return res.json();
}

/** Watches an element's width; null until measured (and in environments without ResizeObserver). */
function useElementWidth(ref: React.RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? null));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

export interface AdminFeatureFlagsPageProps {
  /** `v2` defaults the sort to newest and adds the scope filter. The System tab stays `legacy`. */
  variant?: 'legacy' | 'v2';
  /** Hide the section header when the System page already titled the tab. */
  embedded?: boolean;
}

const AdminFeatureFlagsPage: React.FC<AdminFeatureFlagsPageProps> = ({ variant = 'legacy', embedded = false }) => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const cardWidth = useElementWidth(cardRef);
  const narrow = cardWidth !== null && cardWidth < NARROW_WIDTH;

  // ---- URL state ----------------------------------------------------------
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = searchParams.get('org') || '';
  const areaParam = searchParams.get('area') ?? '';
  const area: FlagArea | '' = (FLAG_AREAS as readonly string[]).includes(areaParam) ? (areaParam as FlagArea) : '';
  const stateTab = pick(STATE_TABS, searchParams.get('state'), 'all');
  const defaultSort: SortKey = variant === 'v2' ? 'new' : 'name';
  const sort = pick(SORTS, searchParams.get('sort'), defaultSort);
  const scopeParam = searchParams.get('fscope') ?? '';
  const flagScope = variant === 'v2' && (['store', 'vendor', 'system'] as const).some((scope) => scope === scopeParam)
    ? (scopeParam as 'store' | 'vendor' | 'system')
    : '';
  const sortOrder: SortKey[] = variant === 'v2' ? ['new', 'old', 'name', 'area'] : [...SORTS];
  const paramDefaults: Record<string, string> = variant === 'v2' ? { state: 'all', sort: 'new' } : DEFAULTS;
  const urlSearch = searchParams.get('q') ?? '';
  const [flagSearch, setFlagSearch] = useState(urlSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch.trim());

  /** Writes params into the query string; an empty or default value drops the param. */
  const setParams = (values: Record<string, string>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [name, value] of Object.entries(values)) {
          if (value && paramDefaults[name] !== value) next.set(name, value);
          else next.delete(name);
        }
        return next;
      },
      { replace: true },
    );

  // Back/forward or in-app navigation can change ?q= under us; follow it unless the input already holds that value.
  useEffect(() => {
    if (urlSearch.trim() !== flagSearch.trim()) {
      setFlagSearch(urlSearch);
      setDebouncedSearch(urlSearch.trim());
    }
    // Only react to the URL; the debounce effect below owns pushing input changes out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const trimmed = flagSearch.trim();
      setDebouncedSearch(trimmed);
      if (trimmed !== urlSearch.trim()) setParams({ q: trimmed });
    }, 200);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagSearch]);

  // ---- Row UI state -------------------------------------------------------
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{ key: string; target: GlobalSetting | null } | null>(null);
  /** Names of organizations picked in this session, so the scope bar needs no lookup for them. */
  const [knownOrgs, setKnownOrgs] = useState<Record<string, ScopedOrganization>>({});
  /**
   * Writes in flight: global ones by flag key, organization ones by `${orgId}:${key}`.
   * A flag's controls stay disabled until its write settles, so two writes to the
   * same setting can never resolve out of order.
   */
  const [pendingGlobal, setPendingGlobal] = useState<Set<string>>(new Set());
  const [pendingOrg, setPendingOrg] = useState<Set<string>>(new Set());
  const withEntry = (set: Set<string>, id: string, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(id);
    else next.delete(id);
    return next;
  };

  useEffect(() => {
    if (!confirm) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setConfirm(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirm]);

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // ---- Server data --------------------------------------------------------
  const registryQuery = useQuery({
    queryKey: REGISTRY_KEY,
    queryFn: async (): Promise<RegistryResponse> => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/admin/feature-flags', { headers });
      if (!res.ok) throw new Error(t('admin.featureFlags.loadError'));
      const body = await res.json();
      return body.data as RegistryResponse;
    },
    staleTime: 60_000,
  });

  /** How many organizations exist, for "Applies to N organizations". */
  const totalOrgsQuery = useQuery({
    queryKey: ['admin-feature-flags-org-total'],
    queryFn: async (): Promise<number | null> => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/admin/organizations?page=1&limit=1', { headers });
      if (!res.ok) return null;
      const body = await res.json();
      return typeof body?.data?.total === 'number' ? body.data.total : null;
    },
    staleTime: 5 * 60_000,
  });
  const totalOrganizations = totalOrgsQuery.data ?? null;

  const definitions = useMemo(() => registryQuery.data?.definitions ?? [], [registryQuery.data]);
  const globalSettings = useMemo(() => registryQuery.data?.global ?? {}, [registryQuery.data]);

  /** Organization names and types already on the page (override lists), by id. */
  const orgsFromOverrides = useMemo(() => {
    const map = new Map<string, ScopedOrganization>();
    const counts = new Map<string, number>();
    for (const def of definitions) {
      for (const entry of def.overrides ?? []) {
        map.set(entry.organizationId, { id: entry.organizationId, name: entry.organizationName, type: entry.organizationType });
        counts.set(entry.organizationId, (counts.get(entry.organizationId) ?? 0) + 1);
      }
    }
    return { map, counts };
  }, [definitions]);

  const knownOrg = orgId ? knownOrgs[orgId] ?? orgsFromOverrides.map.get(orgId) : undefined;
  const orgInfoQuery = useQuery({
    queryKey: ['admin-feature-flags-org-info', orgId],
    queryFn: async (): Promise<ScopedOrganization> => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/admin/organizations/${encodeURIComponent(orgId)}`, { headers });
      if (!res.ok) return { id: orgId, name: orgId, type: null };
      const body = await res.json();
      const data = body?.data ?? {};
      return { id: orgId, name: String(data.displayName || data.name || orgId), type: typeof data.type === 'string' ? data.type : null };
    },
    enabled: !!orgId && !knownOrg,
    staleTime: 5 * 60_000,
  });
  const selectedOrg: ScopedOrganization | null = orgId ? knownOrg ?? orgInfoQuery.data ?? { id: orgId, name: orgId, type: null } : null;

  const orgOverridesQuery = useQuery({
    queryKey: orgKey(orgId || undefined),
    queryFn: async (): Promise<OrgOverridesResponse> => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/admin/feature-flags/organizations/${encodeURIComponent(orgId)}`, { headers });
      if (!res.ok) throw new Error(t('admin.featureFlags.loadOrgError'));
      const body = await res.json();
      return body.data as OrgOverridesResponse;
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });
  const orgOverrides = orgOverridesQuery.data?.overrides;
  /** The scoped organization's overrides have not arrived yet: its counts and "Overridden here" are unknown, not zero. */
  const orgOverridesLoading = !!orgId && !orgOverridesQuery.data && !orgOverridesQuery.isError;
  const orgDetails = useMemo(() => {
    const map = new Map<string, OrgOverridesResponse['resolved']['details'][number]>();
    for (const detail of orgOverridesQuery.data?.resolved.details ?? []) map.set(detail.key, detail);
    return map;
  }, [orgOverridesQuery.data]);

  const selectOrg = (org: ScopedOrganization | null) => {
    if (org) setKnownOrgs((prev) => ({ ...prev, [org.id]: org }));
    setParams({ org: org?.id ?? '' });
  };

  // ---- Mutations (optimistic, rolled back on error) -----------------------
  const invalidateAfterWrite = () => {
    queryClient.invalidateQueries({ queryKey: REGISTRY_KEY });
    queryClient.invalidateQueries({ queryKey: ['admin-feature-flags-org'] });
    queryClient.invalidateQueries({ queryKey: ['admin-feature-flags-overrides'] });
    // The caller's own nav and in-page gates read the shared org-flags key; without this
    // invalidation they lag until useOrgFeatureFlags' own refetch interval fires.
    queryClient.invalidateQueries({ queryKey: ORG_FEATURE_FLAGS_QUERY_KEY });
  };

  const setGlobalMutation = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: GlobalSetting | null }) =>
      putJson(`/admin/feature-flags/${key}`, { enabled }, t('admin.featureFlags.updateError')),
    onMutate: async ({ key, enabled }) => {
      await queryClient.cancelQueries({ queryKey: REGISTRY_KEY });
      const previous = queryClient.getQueryData<RegistryResponse>(REGISTRY_KEY);
      if (previous) {
        const global = { ...previous.global };
        if (enabled === null) delete global[key];
        else global[key] = enabled;
        queryClient.setQueryData<RegistryResponse>(REGISTRY_KEY, {
          global,
          definitions: previous.definitions.map((def) => (def.key === key ? { ...def, globalOverride: enabled } : def)),
        });
      }
      return { previous };
    },
    onError: (error: Error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(REGISTRY_KEY, context.previous);
      showToast(error.message || t('admin.featureFlags.updateError'), 'error');
    },
    onSettled: (_data, _error, { key }) => {
      setPendingGlobal((prev) => withEntry(prev, key, false));
      invalidateAfterWrite();
    },
  });

  const setOrgFlagMutation = useMutation({
    mutationFn: ({ org, key, enabled }: { org: ScopedOrganization; key: string; enabled: boolean | null }) =>
      putJson(
        `/admin/feature-flags/organizations/${encodeURIComponent(org.id)}/${key}`,
        { enabled },
        t('admin.featureFlags.updateError'),
      ),
    onMutate: async ({ org, key, enabled }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: REGISTRY_KEY }),
        queryClient.cancelQueries({ queryKey: orgKey(org.id) }),
      ]);
      const previousRegistry = queryClient.getQueryData<RegistryResponse>(REGISTRY_KEY);
      const previousOrg = queryClient.getQueryData<OrgOverridesResponse>(orgKey(org.id));
      if (previousOrg) {
        const overrides = { ...previousOrg.overrides };
        if (enabled === null) delete overrides[key];
        else overrides[key] = enabled;
        const def = previousRegistry?.definitions.find((d) => d.key === key);
        const inherited = def && previousRegistry ? globalValueOf(def, previousRegistry.global) : null;
        queryClient.setQueryData<OrgOverridesResponse>(orgKey(org.id), {
          overrides,
          resolved: {
            ...previousOrg.resolved,
            details: previousOrg.resolved.details.map((detail) => {
              if (detail.key !== key) return detail;
              if (enabled !== null) return { ...detail, enabled, source: 'organization' };
              // Back to the global value; a `plan` value is only known after the refetch.
              return typeof inherited === 'boolean' ? { ...detail, enabled: inherited, source: 'global' } : detail;
            }),
          },
        });
      }
      if (previousRegistry) {
        queryClient.setQueryData<RegistryResponse>(REGISTRY_KEY, {
          ...previousRegistry,
          definitions: previousRegistry.definitions.map((def) => {
            if (def.key !== key) return def;
            const list = def.overrides ?? [];
            const prior = list.find((entry) => entry.organizationId === org.id);
            const rest = list.filter((entry) => entry.organizationId !== org.id);
            const nextList =
              enabled === null
                ? rest
                : [...rest, { organizationId: org.id, organizationName: org.name, organizationType: org.type, enabled }].sort((a, b) =>
                    a.organizationName.localeCompare(b.organizationName),
                  );
            const count = def.overrideCount ?? list.length;
            const onCount = def.overrideOnCount ?? list.filter((entry) => entry.enabled).length;
            return {
              ...def,
              overrides: nextList,
              overrideCount: count - (prior ? 1 : 0) + (enabled === null ? 0 : 1),
              overrideOnCount: onCount - (prior?.enabled ? 1 : 0) + (enabled === true ? 1 : 0),
            };
          }),
        });
      }
      return { previousRegistry, previousOrg, orgId: org.id };
    },
    onError: (error: Error, _vars, context) => {
      if (context?.previousRegistry) queryClient.setQueryData(REGISTRY_KEY, context.previousRegistry);
      if (context?.previousOrg) queryClient.setQueryData(orgKey(context.orgId), context.previousOrg);
      showToast(error.message || t('admin.featureFlags.updateError'), 'error');
    },
    onSettled: (_data, _error, { org, key }) => {
      setPendingOrg((prev) => withEntry(prev, `${org.id}:${key}`, false));
      invalidateAfterWrite();
    },
  });

  // ---- Filtering ----------------------------------------------------------
  const anyPlan = definitions.some((def) => globalValueOf(def, globalSettings) === 'plan');
  const searched = useMemo(() => {
    const needle = debouncedSearch.toLowerCase();
    return definitions.filter((def) => {
      if (flagScope && !(def.scopes || []).includes(flagScope)) return false;
      if (area && def.area !== area) return false;
      if (!needle) return true;
      const parts = flagDescriptionParts(def);
      return `${def.key} ${def.label} ${parts.on} ${parts.off} ${def.description}`.toLowerCase().includes(needle);
    });
  }, [definitions, debouncedSearch, area, flagScope]);

  const inTab = (def: FeatureFlagDefinition, tab: StateTab): boolean => {
    const value = globalValueOf(def, globalSettings);
    switch (tab) {
      case 'on':
        return value === true;
      case 'off':
        return value === false;
      case 'plan':
        return value === 'plan';
      case 'ov':
        return selectedOrg ? orgOverrides?.[def.key] !== undefined : (def.overrideCount ?? def.overrides?.length ?? 0) > 0;
      default:
        return true;
    }
  };
  const activeTab: StateTab = stateTab === 'plan' && !anyPlan ? 'all' : stateTab;

  const visible = useMemo(() => {
    const byName = (a: FeatureFlagDefinition, b: FeatureFlagDefinition) => a.label.localeCompare(b.label);
    const byAdded = (a: FeatureFlagDefinition, b: FeatureFlagDefinition) => (a.addedOn ?? '').localeCompare(b.addedOn ?? '');
    const areaLabel = (def: FeatureFlagDefinition) => (def.area ? t(`admin.featureFlags.areas.${def.area}`) : '');
    return searched
      .filter((def) => inTab(def, activeTab))
      .sort((a, b) => {
        switch (sort) {
          case 'new':
            return byAdded(b, a) || byName(a, b);
          case 'old':
            return byAdded(a, b) || byName(a, b);
          case 'area':
            return areaLabel(a).localeCompare(areaLabel(b)) || byName(a, b);
          default:
            return byName(a, b);
        }
      });
    // inTab reads globalSettings, orgOverrides and the selected org.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, activeTab, sort, globalSettings, orgOverrides, orgId, t]);

  const tabOptions = STATE_TABS.filter((tab) => tab !== 'plan' || anyPlan).map((tab) => ({
    value: tab,
    label:
      tab === 'ov'
        ? selectedOrg
          ? t('admin.featureFlags.tabs.ovOrg')
          : t('admin.featureFlags.tabs.ov')
        : t(`admin.featureFlags.tabs.${tab}`),
    count: tab === 'ov' && orgOverridesLoading ? undefined : searched.filter((def) => inTab(def, tab)).length,
  }));

  const areasPresent = FLAG_AREAS.filter((value) => definitions.some((def) => def.area === value));

  const resetFilters = () => {
    setFlagSearch('');
    setDebouncedSearch('');
    setParams({ q: '', area: '', state: '', fscope: '' });
  };

  const scopeCounts = { store: 0, vendor: 0, system: 0 } as Record<'store' | 'vendor' | 'system', number>;
  for (const def of definitions) {
    for (const scope of def.scopes || []) scopeCounts[scope] += 1;
  }

  const orgOverrideCount = orgOverrides ? Object.keys(orgOverrides).length : 0;
  const hint = selectedOrg
    ? orgOverridesLoading
      ? ''
      : t('admin.featureFlags.scope.hintOrg', { count: orgOverrideCount })
    : totalOrganizations !== null
      ? t('admin.featureFlags.scope.hintNone', { count: totalOrganizations })
      : t('admin.featureFlags.scope.hintNoneUnknown');

  const selectSx = {
    height: 34,
    borderRadius: '8px',
    fontSize: 13,
    bgcolor: tokens.surface.card,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.line.default },
    '& .MuiSelect-select': { py: 0, pl: 1.25, display: 'flex', alignItems: 'center' },
  };

  return (
    <PageContainer>
      {!embedded && <PageHeader variant="section" title={t('admin.featureFlags.title')} description={t('admin.featureFlags.description')} />}

      {registryQuery.isError ? (
        <InlineMessage severity="error">{(registryQuery.error as Error)?.message || t('admin.featureFlags.loadError')}</InlineMessage>
      ) : (
        <Box ref={cardRef}>
          <SectionCard dense sx={{ borderRadius: `${tokens.radius.panel}px`, overflow: 'visible' }}>
            {/* 1. Scope bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                px: '18px',
                py: '14px',
                borderBottom: `1px solid ${tokens.line.hairline}`,
              }}
            >
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.body }}>
                {t('admin.featureFlags.scope.label')}
              </Typography>
              <OrganizationPicker value={selectedOrg} onChange={selectOrg} overrideCounts={orgsFromOverrides.counts} />
              {selectedOrg && (
                <>
                  <ButtonBase
                    onClick={() => selectOrg(null)}
                    sx={{ fontSize: 13, fontWeight: 600, color: tokens.accent.main, '&:hover': { color: tokens.accent.hover } }}
                  >
                    {t('admin.featureFlags.scope.clear')}
                  </ButtonBase>
                  <Link component={RouterLink} to={organizationAdminPath(selectedOrg.id)} sx={{ fontSize: 13, fontWeight: 600 }}>
                    {t('admin.featureFlags.scope.open')}
                  </Link>
                </>
              )}
              <Typography component="span" role="status" sx={{ flex: '1 1 200px', fontSize: 12.5, color: tokens.ink.muted }}>
                {hint}
              </Typography>
            </Box>

            {/* 2. Filter bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: '18px', py: 1.5 }}>
              <SegmentedControl
                kind="filter"
                aria-label={t('admin.featureFlags.tabs.label')}
                value={activeTab}
                onChange={(value) => setParams({ state: value })}
                options={tabOptions}
              />
              <Box sx={{ flex: '1 1 520px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    flex: '1 1 200px',
                    minWidth: 180,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    height: 34,
                    px: 1.375,
                    border: `1px solid ${tokens.line.default}`,
                    borderRadius: '9px',
                    bgcolor: tokens.surface.card,
                  }}
                >
                  <SearchIcon sx={{ fontSize: 16, color: tokens.ink.faint }} />
                  <InputBase
                    value={flagSearch}
                    onChange={(event) => setFlagSearch(event.target.value)}
                    placeholder={t('admin.featureFlags.filter.searchPlaceholder')}
                    inputProps={{ 'aria-label': t('admin.featureFlags.filter.searchLabel') }}
                    sx={{ flex: 1, minWidth: 0, fontSize: 13 }}
                  />
                  {flagSearch && (
                    <IconButton size="small" aria-label={t('admin.featureFlags.filter.clearSearch')} onClick={() => setFlagSearch('')} sx={{ p: 0.25 }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
                {variant === 'v2' && (
                  <Select
                    size="small"
                    value={flagScope}
                    displayEmpty
                    onChange={(event) => setParams({ fscope: event.target.value })}
                    inputProps={{ 'aria-label': t('admin.featureFlags.filter.scope') }}
                    sx={selectSx}
                  >
                    <MenuItem value="">{t('admin.featureFlags.filter.allScopes')}</MenuItem>
                    {(['store', 'vendor', 'system'] as const).map((scope) => (
                      <MenuItem key={scope} value={scope}>
                        {t(`admin.featureFlags.scopes.${scope}`)} ({scopeCounts[scope]})
                      </MenuItem>
                    ))}
                  </Select>
                )}
                <Select
                  size="small"
                  value={area}
                  displayEmpty
                  onChange={(event) => setParams({ area: event.target.value })}
                  inputProps={{ 'aria-label': t('admin.featureFlags.filter.area') }}
                  sx={selectSx}
                >
                  <MenuItem value="">{t('admin.featureFlags.filter.allAreas')}</MenuItem>
                  {areasPresent.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`admin.featureFlags.areas.${value}`)}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  size="small"
                  value={sort}
                  onChange={(event) => setParams({ sort: event.target.value })}
                  inputProps={{ 'aria-label': t('admin.featureFlags.sort.label') }}
                  sx={selectSx}
                >
                  {sortOrder.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`admin.featureFlags.sort.${value}`)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            {/* 3. List */}
            {registryQuery.isLoading || (activeTab === 'ov' && orgOverridesLoading) ? (
              <TableSkeleton rows={6} columns={3} />
            ) : definitions.length === 0 ? (
              <EmptyState size="compact" title={t('admin.featureFlags.emptyTitle')} description={t('admin.featureFlags.emptyDescription')} />
            ) : (
              <>
                {!narrow && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'minmax(0,1fr)', md: FLAG_GRID_COLUMNS },
                      alignItems: 'center',
                      gap: 2,
                      px: '18px',
                      height: 34,
                      borderTop: `1px solid ${tokens.line.hairline}`,
                      borderBottom: `1px solid ${tokens.line.hairline}`,
                      bgcolor: tokens.surface.sunken,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: tokens.ink.muted,
                    }}
                  >
                    <Box component="span" sx={{ minWidth: 0 }}>
                      {t('admin.featureFlags.columns.flag', { shown: visible.length, total: definitions.length })}
                    </Box>
                    <span>{t('admin.featureFlags.columns.global')}</span>
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedOrg
                        ? t('admin.featureFlags.columns.overrideFor', { org: selectedOrg.name })
                        : t('admin.featureFlags.columns.overrides')}
                    </Box>
                  </Box>
                )}
                {narrow && (
                  <Typography sx={{ px: '18px', py: 1, fontSize: 12, color: tokens.ink.muted, borderTop: `1px solid ${tokens.line.hairline}` }}>
                    {t('admin.featureFlags.columns.flag', { shown: visible.length, total: definitions.length })}
                  </Typography>
                )}
                {visible.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6, px: '18px' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('admin.featureFlags.filter.noMatchTitle')}</Typography>
                    <ButtonBase onClick={resetFilters} sx={{ fontSize: 13, fontWeight: 600, color: tokens.accent.main }}>
                      {t('admin.featureFlags.filter.reset')}
                    </ButtonBase>
                  </Box>
                ) : (
                  visible.map((def) => {
                    const setting = globalSettings[def.key];
                    return (
                      <FlagRow
                        key={def.key}
                        def={def}
                        globalSetting={setting === undefined ? null : setting}
                        globalValue={globalValueOf(def, globalSettings)}
                        narrow={narrow}
                        expanded={expanded.has(def.key)}
                        onToggleExpanded={() => toggleExpanded(def.key)}
                        org={selectedOrg}
                        orgOverride={orgOverrides?.[def.key]}
                        orgDetail={orgDetails.get(def.key)}
                        orgLoading={!!orgId && orgOverridesQuery.isLoading}
                        orgPending={!!selectedOrg && pendingOrg.has(`${selectedOrg.id}:${def.key}`)}
                        globalPending={pendingGlobal.has(def.key)}
                        onOrgOverride={(enabled) => {
                          if (!selectedOrg) return;
                          const id = `${selectedOrg.id}:${def.key}`;
                          if (pendingOrg.has(id)) return;
                          setPendingOrg((prev) => withEntry(prev, id, true));
                          setOrgFlagMutation.mutate({ org: selectedOrg, key: def.key, enabled });
                        }}
                        onPickOrg={selectOrg}
                        confirmTarget={confirm?.key === def.key ? confirm.target : undefined}
                        onAskGlobal={(target) => {
                          if (pendingGlobal.has(def.key)) return;
                          setConfirm((prev) => (prev?.key === def.key && prev.target === target ? null : { key: def.key, target }));
                        }}
                        onCancelGlobal={() => setConfirm(null)}
                        showScopes={variant === 'v2'}
                        onConfirmGlobal={() => {
                          if (!confirm || pendingGlobal.has(confirm.key)) return;
                          setPendingGlobal((prev) => withEntry(prev, confirm.key, true));
                          setGlobalMutation.mutate({ key: confirm.key, enabled: confirm.target });
                          setConfirm(null);
                        }}
                        totalOrganizations={totalOrganizations}
                      />
                    );
                  })
                )}
              </>
            )}
          </SectionCard>
        </Box>
      )}

      {orgOverridesQuery.isError && (
        <Box sx={{ mt: 2 }}>
          <InlineMessage severity="error">
            {(orgOverridesQuery.error as Error)?.message || t('admin.featureFlags.loadOrgError')}
          </InlineMessage>
        </Box>
      )}
    </PageContainer>
  );
};

export default AdminFeatureFlagsPage;
